import { useEffect, useRef } from "react";
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import Landing from "@/components/Landing";
import SimulationsLibrary from "@/components/SimulationsLibrary";
import SimulationRoute from "@/components/SimulationRoute";
import HowItWorks from "@/components/HowItWorks";
import About from "@/components/About";
import Contact from "@/components/Contact";
import PrivacyPolicy from "@/components/PrivacyPolicy";
import TermsOfUse from "@/components/TermsOfUse";
import Admin from "@/components/Admin";
import SignIn from "@/components/SignIn";
import GoogleCallback from "@/components/GoogleCallback";
import MyAdaptience from "@/components/MyAdaptience";
import PracticeHistoryReport from "@/components/PracticeHistoryReport";
import AccountSettings from "@/components/AccountSettings";
import { CATEGORIES } from "@/data/simulations";
import { track } from "@/lib/analytics";
import DevSimulationEntry from "@/simulations/DevSimulationEntry";
import DevReportEntry from "@/simulations/DevReportEntry";

// Route wrappers connect the existing prop-based page components to router navigation.
function ContactPage() {
  const navigate = useNavigate();
  return (
    <Contact
      onBack={() => navigate("/")}
      onHome={() => navigate("/")}
      onPrivacy={() => navigate("/privacy")}
      onTerms={() => navigate("/terms")}
      onContact={() => document.querySelector('[data-testid="contact-page"]')?.scrollTo({ top: 0, behavior: "smooth" })}
    />
  );
}

function PrivacyPage() {
  const navigate = useNavigate();
  return <PrivacyPolicy onBack={() => navigate("/")} onHome={() => navigate("/")} />;
}

function TermsPage() {
  const navigate = useNavigate();
  return <TermsOfUse onBack={() => navigate("/")} onHome={() => navigate("/")} />;
}

function App() {
  const location = useLocation();
  const isAdmin = location.pathname.replace(/\/+$/, "") === "/admin";

  // Preserve existing analytics: fire visit once per app load (non-admin),
  // and homepage_view only when the homepage itself is viewed (accurate funnel attribution).
  const trackedVisit = useRef(false);
  const trackedHome = useRef(false);
  useEffect(() => {
    if (isAdmin) return;
    if (!trackedVisit.current) {
      trackedVisit.current = true;
      track("visit");
    }
    if (location.pathname === "/" && !trackedHome.current) {
      trackedHome.current = true;
      track("homepage_view");
    }
  }, [isAdmin, location.pathname]);

  return (
    <div className="App relative min-h-[100dvh] w-full bg-[#0a0a0a]">
      <Toaster position="top-center" />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/simulations" element={<SimulationsLibrary />} />
        {/* Six permanent clean category URLs — each renders the existing Library
            filtered to that category (data-driven from the frozen catalogue). */}
        {CATEGORIES.map((c) => (
          <Route key={c.slug} path={`/simulations/${c.slug}`} element={<SimulationsLibrary />} />
        ))}
        <Route path="/simulations/:slug/preview" element={<SimulationsLibrary />} />
        <Route path="/simulations/:slug" element={<SimulationRoute />} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/signin" element={<SignIn />} />
        <Route path="/auth/google" element={<GoogleCallback />} />
        <Route path="/my-adaptience" element={<MyAdaptience />} />
        <Route path="/my-adaptience/report/:reportId" element={<PracticeHistoryReport />} />
        <Route path="/account/settings" element={<AccountSettings />} />
        {/* Development-only entry for coming-soon simulations (tree-shaken out of production builds). */}
        {process.env.NODE_ENV === "development" && <Route path="/dev/simulation/:slug" element={<DevSimulationEntry />} />}
        {process.env.NODE_ENV === "development" && <Route path="/dev/report/:reportId" element={<DevReportEntry />} />}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
